import type { Question } from '@/types';
import type { MappedAnswers, SliderType } from './slider.types';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import {
  applyModifiers,
  changeRoot,
  dateMonthStyle,
  filter,
  finalValues,
  getDefaultValue,
  initializeValidation,
} from './slider.utils';
import Condition from '@/utils/condition';

const initialState = {
  answers: {
    direct: {},
    default: {},
  },
  questions: {
    all: [],
    visible: [],
  },
  validation: {},
  isFormSubmitted: false,
  isFormSubmissionSuccessful: false,
  isFormSubmissionFailed: false,
  currentId: 0,
} satisfies SliderType as SliderType;

const SliderSlice = createSlice({
  name: 'slider',
  initialState,
  reducers: (create) => ({
    setAnswersDirectByKey: create.reducer<KeyValue>((state, action) => {
      state.answers.direct[action.payload.key] = action.payload.value;
    }),
    resetAnswersDirect: create.reducer((state) => {
      state.answers.direct = initialState.answers.direct;
    }),
    setAnswersDefaultByKey: create.reducer<KeyValue>((state, action) => {
      const question = state.questions.all.find((q) => q.key === action.payload.key);
      if (question && question.validate) {
        state.validation[action.payload.key].messages = question.validate.fn(action.payload.value);
      }
      state.answers.default[action.payload.key] = action.payload.value;
    }),
    setIsFormSubmitted: create.reducer<SliderType['isFormSubmitted']>((state, action) => {
      state.isFormSubmitted = action.payload;
    }),
    setIsFormSubmissionSuccessful: create.reducer<SliderType['isFormSubmissionSuccessful']>((state, action) => {
      state.isFormSubmissionSuccessful = action.payload;
    }),
    setIsFormSubmissionFailed: create.reducer<SliderType['isFormSubmissionFailed']>((state, action) => {
      state.isFormSubmissionFailed = action.payload;
    }),
    setQuestionsVisible: create.reducer<SliderType['questions']['visible']>((state, action) => {
      state.questions.visible = action.payload;
    }),
    setCurrentQuestionId: create.reducer<number>((state, action) => {
      if (state.questions.visible[action.payload].type === 'submission') {
        const modifiers = [finalValues, dateMonthStyle];
        state.answers.direct = applyModifiers(state.questions.all, state.answers.direct, modifiers);
        state.answers.default = applyModifiers(state.questions.all, state.answers.default, modifiers);
      }
      state.currentId = action.payload;
    }),
    resetCurrentQuestionId: create.reducer((state) => {
      state.currentId = initialState.currentId;
    }),
    setValidationDirtyByKey: create.reducer<string>((state, action) => {
      state.validation[action.payload].dirty = true;
    }),
    fillSliderSliceWithInitialData: create.reducer<Question[]>((state, action) => {
      const { rootChanged, questions } = changeRoot(action.payload);

      if (rootChanged) {
        state.answers.default[rootChanged.key] = rootChanged.value;
        state.answers.direct[rootChanged.key] = rootChanged.value;
      }

      state.questions.all = questions;
      state.questions.visible = filter(questions);
      state.validation = initializeValidation(questions);
    }),
    prepareSliderForNextStep: create.reducer<string>((state, action) => {
      const value = state.answers.default[action.payload] || '';
      const question = state.questions.all.find((q) => q.key === action.payload);
      const defaultValue = getDefaultValue(question);
      const canAssignDefaultValue = Condition.isEmpty(value) && defaultValue !== null;
      state.answers.direct[action.payload] = canAssignDefaultValue ? defaultValue : value;
      state.answers.default[action.payload] = canAssignDefaultValue ? defaultValue : value;
      state.validation[action.payload].messages = question?.validate ? question.validate.fn(value) : [];
    }),
    toInitialState: create.reducer(() => initialState),
  }),
  selectors: {
    slider: (slider) => slider,
    answers: (slider) => slider.answers,
    answersDirect: (slider) => slider.answers.direct,
    answersDefault: (slider) => slider.answers.default,
    questions: (slider) => slider.questions,
    questionsAll: (slider) => slider.questions.all,
    questionsVisible: (slider) => slider.questions.visible,
    validation: (slider) => slider.validation,
    currentId: (slider) => slider.currentId,
    currentQuestionKey: (slider) => {
      const list = slider.questions.visible;
      const id = slider.currentId;
      return list[id] ? list[id].key : '';
    },
    isFormSubmissionFailed: (slider) => slider.isFormSubmissionFailed,
    isFormSubmissionSuccessful: (slider) => slider.isFormSubmissionSuccessful,
    isFormSubmitted: (slider) => slider.isFormSubmitted,
    mappedAnswers: createSelector(
      [(slider: SliderType) => slider.answers.direct, (slider: SliderType) => slider.questions.all],
      (answersDirect, questionsAll) => {
        return Object.keys(answersDirect).reduce((obj, key) => {
          let label;
          let value;

          const question = questionsAll.find((q) => q.key === key);

          if (question && question.choices && key !== 'zip') {
            const questionAnswer = question.choices.find((c) => c.value === answersDirect[key]);
            label = questionAnswer ? questionAnswer.label : answersDirect[key];
            value = questionAnswer ? questionAnswer.value : answersDirect[key];
          } else {
            label = answersDirect[key];
            value = answersDirect[key];
          }

          obj[key] = { label, value };
          return obj;
        }, {} as MappedAnswers);
      },
    ),
  },
});

export default SliderSlice;
